import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { TrendingUp, Lock, Loader2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { Alert, AlertDescription } from "../components/ui/alert";
import { api } from "../context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

// Misma pantalla para dos caminos: recuperar una contraseña olvidada y crear la
// primera al aceptar una invitación. El formulario, la validación y el POST son
// idénticos —los dos consumen un token de un solo uso contra
// `/auth/reset-password`—, así que lo único que cambia son los textos. Duplicar el
// componente para cambiar tres frases significaría arreglar cada bug dos veces.
const TEXTOS = {
    reset: {
        titulo: "auth.resetPasswordTitle",
        bajada: "auth.resetPasswordDesc",
        exito: "auth.resetSuccess",
        vencido: "auth.tokenExpiredDesc",
        boton: "auth.resetPasswordBtn",
    },
    invitacion: {
        titulo: "auth.setPasswordTitle",
        bajada: "auth.setPasswordDesc",
        exito: "auth.setPasswordSuccess",
        vencido: "auth.inviteExpiredDesc",
        boton: "auth.setPasswordBtn",
    },
};

export default function ResetPassword({ modo = "reset" }) {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const textos = TEXTOS[modo] || TEXTOS.reset;

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [tokenError, setTokenError] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    // No token in URL — show error immediately
    if (!token) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-flex items-center gap-2">
                            <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-white" strokeWidth={1.5} />
                            </div>
                            <span className="font-heading font-semibold text-xl text-foreground">True Flow</span>
                        </Link>
                    </div>
                    <Card className="shadow-sm">
                        <CardContent className="pt-6 space-y-4">
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>{t(textos.vencido)}</AlertDescription>
                            </Alert>
                            <Link to="/forgot-password">
                                <Button className="w-full">{t("auth.requestNewLink")}</Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newPassword || !confirmPassword) {
            toast.error(t("common.required"));
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error(t("auth.passwords.matchError"));
            return;
        }
        if (newPassword.length < 8) {
            toast.error(t("auth.passwords.newPlaceholder"));
            return;
        }

        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: newPassword });
            toast.success(t(textos.exito));
            navigate("/login");
        } catch (error) {
            const detail = error.response?.data?.detail || "";
            // Distinguish expired vs invalid vs other
            if (error.response?.status === 400) {
                setTokenError(detail);
            } else {
                toast.error(detail || t("errors.generic"));
            }
        } finally {
            setLoading(false);
        }
    };

    // Token was rejected by backend (expired or already used)
    if (tokenError) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-flex items-center gap-2">
                            <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-white" strokeWidth={1.5} />
                            </div>
                            <span className="font-heading font-semibold text-xl text-foreground">True Flow</span>
                        </Link>
                    </div>
                    <Card className="shadow-sm">
                        <CardHeader className="text-center">
                            <CardTitle className="font-heading text-xl">{t("auth.tokenExpiredTitle")}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>{tokenError}</AlertDescription>
                            </Alert>
                            <Link to="/forgot-password">
                                <Button className="w-full">{t("auth.requestNewLink")}</Button>
                            </Link>
                            <p className="text-center text-sm text-muted-foreground">
                                <Link to="/login" className="text-primary hover:underline font-medium">
                                    {t("auth.backToLogin")}
                                </Link>
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-2">
                        <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-white" strokeWidth={1.5} />
                        </div>
                        <span className="font-heading font-semibold text-xl text-foreground">True Flow</span>
                    </Link>
                </div>

                <Card className="shadow-sm">
                    <CardHeader className="text-center">
                        <CardTitle className="font-heading text-2xl">{t(textos.titulo)}</CardTitle>
                        <CardDescription>{t(textos.bajada)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="new-password">{t("auth.passwords.new")}</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="new-password"
                                        type={showNew ? "text" : "password"}
                                        placeholder={t("auth.passwords.newPlaceholder")}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="pl-10 pr-10"
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNew(!showNew)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        tabIndex={-1}
                                    >
                                        {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirm-password">{t("auth.passwords.confirm")}</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="confirm-password"
                                        type={showConfirm ? "text" : "password"}
                                        placeholder={t("auth.passwords.confirmPlaceholder")}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="pl-10 pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirm(!showConfirm)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        tabIndex={-1}
                                    >
                                        {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <Button type="submit" className="w-full" disabled={loading}>
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t("common.loading")}
                                    </>
                                ) : (
                                    t(textos.boton)
                                )}
                            </Button>
                        </form>

                        <p className="text-center text-sm text-muted-foreground mt-4">
                            <Link to="/login" className="text-primary hover:underline font-medium">
                                {t("auth.backToLogin")}
                            </Link>
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
